/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { useToast } from '@ecsper/ui-primitives';

const STAGE_6_MESSAGE = 'Available in Stage 6';

export interface Stage6StubsProps {
  /** Layout direction. Default 'vertical' for task pane. */
  layout?: 'horizontal' | 'vertical';
}

function SparkIcon() {
  return (
    <svg
      className="ecs-btn__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v3M12 18v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M3 12h3M18 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"
      />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function QuoteIcon() {
  return (
    <svg
      className="ecs-btn__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M7 10a4 4 0 014-4v4a4 4 0 01-4 4H5v-4h2zM17 10a4 4 0 014-4v4a4 4 0 01-4 4h-2v-4h2z" />
    </svg>
  );
}

function VaultIcon() {
  return (
    <svg
      className="ecs-btn__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="5" y="9" width="14" height="11" rx="2" />
      <path strokeLinecap="round" d="M8 9V7a4 4 0 018 0v2" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      className="ecs-btn__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M12 3l8 4v6c0 5-3.5 8.5-8 9.5C7.5 21.5 4 18 4 13V7l8-4z" />
      <path strokeLinecap="round" d="M9 12l2 2 4-4" />
    </svg>
  );
}

/**
 * Placeholder actions for Stage 6 roadmap features. Buttons stay visually
 * enabled and explain availability via toast (TrueRead T6 predictable behaviour).
 */
export function Stage6Stubs({ layout = 'vertical' }: Stage6StubsProps) {
  const { toast } = useToast();

  const rootClass =
    layout === 'horizontal' ? 'ecs-stage6-stubs ecs-stage6-stubs--horizontal' : 'ecs-stage6-stubs';

  const notify = (): void => {
    toast(STAGE_6_MESSAGE, 'info');
  };

  return (
    <div className={rootClass}>
      <button
        type="button"
        className="ecs-btn ecs-btn--secondary ecs-stage6-stubs__button"
        onClick={notify}
      >
        <SparkIcon />
        AI Assist
      </button>
      <button
        type="button"
        className="ecs-btn ecs-btn--secondary ecs-stage6-stubs__button"
        onClick={notify}
      >
        <QuoteIcon />
        Insert Citation
      </button>
      <button
        type="button"
        className="ecs-btn ecs-btn--secondary ecs-stage6-stubs__button"
        onClick={notify}
      >
        <VaultIcon />
        Insert Vault Ref
      </button>
      <button
        type="button"
        className="ecs-btn ecs-btn--secondary ecs-stage6-stubs__button"
        onClick={notify}
      >
        <ShieldIcon />
        Compliance Check
      </button>
    </div>
  );
}
